import { useEffect, useMemo, useState } from "react";
import styles from "./SocialMediaReportViewer.module.css";
import {
  getActiveSocialWindow,
  getSocialWindowList,
} from "./helpers/socialMediaWindows";

const EMPTY = "—";

const safeNum = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const fmt = {
  int: (value) => {
    const num = safeNum(value);
    return num !== null
      ? new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(num)
      : EMPTY;
  },
  dec: (value, digits = 1) => {
    const num = safeNum(value);
    return num !== null ? num.toFixed(digits) : EMPTY;
  },
  pct: (value, digits = 1) => {
    const num = safeNum(value);
    return num !== null ? `${(num * 100).toFixed(digits)}%` : EMPTY;
  },
  date: (value) => {
    if (!value) return EMPTY;
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? EMPTY
      : date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  },
};

const getInitials = (value = "") =>
  String(value || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "SM";

const buildCadenceFlag = (postingCadence = {}) => {
  if (postingCadence?.flag) return postingCadence.flag;
  const gaps = Array.isArray(postingCadence?.gaps) ? postingCadence.gaps : [];
  if (!gaps.length) return "No multi-day posting gap detected in this window.";
  const first = gaps[0];
  return `${first.days}-day gap detected between ${fmt.date(first.start)} and ${fmt.date(first.end)}`;
};

const buildLegacyReportTab = (platform = null) => {
  if (!platform) return null;
  const isInstagram = platform.platform === "instagram";
  const summary = platform?.content_summary || {};
  return {
    key: platform.platform,
    label: isInstagram ? "Instagram" : "TikTok",
    account_header: {
      ...(platform.profile || {}),
      stats: isInstagram
        ? {
            followers: safeNum(platform?.profile?.followers),
            following: safeNum(platform?.profile?.following),
            posts_total: safeNum(platform?.profile?.posts_total),
          }
        : {
            followers: safeNum(platform?.profile?.followers),
            likes_total: safeNum(platform?.profile?.likes_total),
            posts_total: safeNum(platform?.profile?.posts_total),
          },
    },
    growth_snapshot: platform.growth_snapshot || {},
    posting_cadence: {
      ...(platform.posting_cadence || {}),
      flag: buildCadenceFlag(platform.posting_cadence),
    },
    summary_strip: isInstagram
      ? [
          { label: "Total likes", value: summary.total_likes },
          { label: "Total comments", value: summary.total_comments },
          { label: "Avg likes/post", value: summary.avg_likes_per_post, format: "decimal" },
          { label: "Avg eng rate", value: summary.avg_engagement_rate, format: "percent" },
          { label: "Image posts", value: summary.image_posts },
          { label: "Carousel/Video", value: summary.carousel_or_video },
        ]
      : [
          { label: "Total views", value: summary.total_views },
          { label: "Total likes", value: summary.total_likes },
          { label: "Total comments", value: summary.total_comments },
          { label: "Total shares", value: summary.total_shares },
          { label: "Avg engagement rate", value: summary.avg_engagement_rate, format: "percent" },
          { label: "Engagement per view", value: summary.engagement_per_view, format: "percent" },
        ],
    content_performance_summary: {
      summary,
      best_post: platform?.best_post || null,
      worst_post: platform?.worst_post || null,
      latest_posts: Array.isArray(platform?.posts) ? platform.posts : [],
    },
    all_posts_performance: {
      rows: Array.isArray(platform?.posts) ? platform.posts : [],
      sort: "most_recent",
    },
  };
};

const renderMetricValue = (item = {}) => {
  if (item?.format === "percent") return fmt.pct(item.value);
  if (item?.format === "decimal") return fmt.dec(item.value);
  return fmt.int(item.value);
};

const TabIcon = ({ type }) =>
  type === "instagram" ? (
    <svg className={styles.tabIcon} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
    </svg>
  ) : (
    <svg className={styles.tabIcon} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005.8 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" />
    </svg>
  );

const PostThumb = ({ post = {}, square = false }) => {
  const url = typeof post?.media_url === "string" ? post.media_url.trim() : "";
  return url ? (
    <img
      className={`${styles.postThumb} ${square ? styles.postThumbIg : ""}`}
      src={url}
      alt={post?.title || post?.caption || "Post media"}
    />
  ) : (
    <div className={`${styles.postThumb} ${square ? styles.postThumbIg : ""} ${styles.postThumbFallback}`} />
  );
};

function BestWorstCard({ title, post, isInstagram = false, worst = false }) {
  const href = post?.permalink || null;
  const Wrap = href ? "a" : "div";
  const wrapProps = href
    ? { href, target: "_blank", rel: "noopener noreferrer", className: `${styles.bwCard} ${worst ? styles.bwCardWorst : styles.bwCardBest} ${styles.bwCardLink}` }
    : { className: `${styles.bwCard} ${worst ? styles.bwCardWorst : styles.bwCardBest}` };

  return (
    <Wrap {...wrapProps}>
      <div className={styles.bwHdr}>
        {title}
        {href ? <span className={styles.bwLinkHint}>↗ View post</span> : null}
      </div>
      {post ? (
        <div className={styles.bwBody}>
          <PostThumb post={post} square={isInstagram} />
          <div className={styles.bwContent}>
            <div className={styles.postCaption}>{post.caption || post.title || "Untitled post"}</div>
            <div className={styles.bwMetrics}>
              {!isInstagram && safeNum(post.views) !== null ? <span><strong>{fmt.int(post.views)}</strong> views</span> : null}
              {safeNum(post.likes) !== null ? <span><strong>{fmt.int(post.likes)}</strong> likes</span> : null}
              {safeNum(post.comments) !== null ? <span><strong>{fmt.int(post.comments)}</strong> comments</span> : null}
              {!isInstagram && safeNum(post.shares) !== null ? <span><strong>{fmt.int(post.shares)}</strong> shares</span> : null}
              {safeNum(post.engagement_rate) !== null ? <span className={styles.bestWorstAccent}>{fmt.pct(post.engagement_rate, 2)} eng. rate</span> : null}
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.emptyState}>No post detected in this window.</div>
      )}
    </Wrap>
  );
}

const POSTS_PAGE_SIZE = 10;

function PlatformView({ tab, windowLabel = "this window" }) {
  const isInstagram = tab.key === "instagram";
  const header = tab.account_header || {};
  const stats = header.stats || {};
  const growth = tab.growth_snapshot || {};
  const cadence = tab.posting_cadence || {};
  const summaryStrip = Array.isArray(tab.summary_strip) ? tab.summary_strip : [];
  const perf = tab.content_performance_summary || {};
  const posts = Array.isArray(tab.all_posts_performance?.rows)
    ? tab.all_posts_performance.rows
    : Array.isArray(perf.latest_posts)
      ? perf.latest_posts
      : [];
  const cadenceDays = Array.isArray(cadence.days) ? cadence.days : [];

  const [visibleCount, setVisibleCount] = useState(POSTS_PAGE_SIZE);
  useEffect(() => { setVisibleCount(POSTS_PAGE_SIZE); }, [tab]);
  const visiblePosts = posts.slice(0, visibleCount);
  const hasMore = visibleCount < posts.length;

  return (
    <div className={styles.tabView}>
      <div className={styles.accountCard}>
        <div className={styles.accountAvatar}>
          {header.avatar_url ? (
            <img src={header.avatar_url} alt={header.display_name || "Profile"} className={styles.accountAvatarImage} />
          ) : (
            getInitials(header.display_name || header.handle)
          )}
        </div>
        <div className={styles.accountInfo}>
          <div className={styles.accountName}>{header.display_name || EMPTY}</div>
          <div className={styles.accountHandle}>{header.handle || EMPTY}</div>
          <div className={styles.accountBio}>{header.bio || "No bio captured."}</div>
        </div>
        <div className={styles.accountStats}>
          <div className={styles.accountStat}><div className={styles.accountStatVal}>{fmt.int(stats.followers)}</div><div className={styles.accountStatLbl}>Followers</div></div>
          <div className={styles.accountStat}><div className={styles.accountStatVal}>{fmt.int(isInstagram ? stats.following : stats.likes_total)}</div><div className={styles.accountStatLbl}>{isInstagram ? "Following" : "Likes"}</div></div>
          <div className={styles.accountStat}><div className={styles.accountStatVal}>{fmt.int(stats.posts_total)}</div><div className={styles.accountStatLbl}>{isInstagram ? "Posts" : "Videos"}</div></div>
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionHdr}>
          <div className={styles.sectionTitle}>Growth snapshot</div>
          <div className={styles.sectionSub}>{windowLabel}</div>
        </div>
        <div className={`${styles.mg} ${styles.mgC5}`}>
          <div className={styles.mc}><div className={styles.ml}>Followers today</div><div className={styles.mv}>{fmt.int(growth.followers_today ?? stats.followers)}</div><div className={styles.mch}>{growth.note || "Historical deltas unavailable"}</div></div>
          <div className={styles.mc}><div className={styles.ml}>7 days ago</div><div className={styles.mv}>{fmt.int(growth.followers_7_days_ago)}</div></div>
          <div className={styles.mc}><div className={styles.ml}>28 days ago</div><div className={styles.mv}>{fmt.int(growth.followers_28_days_ago)}</div></div>
          <div className={styles.mc}><div className={styles.ml}>Posts published</div><div className={styles.mv}>{fmt.int(growth.posts_published)}</div></div>
          <div className={styles.mc}><div className={styles.ml}>Cadence</div><div className={styles.mv}>{fmt.dec(growth.cadence_per_week)}</div><div className={styles.mch}>posts/week</div></div>
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionHdr}>
          <div className={styles.sectionTitle}>Posting cadence</div>
          <div className={styles.sectionSub}>{windowLabel} · each square = 1 posted day</div>
        </div>
        <div className={styles.cadenceBox}>
          <div className={styles.cadenceGrid}>
            {cadenceDays.map((day) => (
              <div
                key={day.date}
                className={`${styles.cadenceDay} ${
                  day.count >= 2
                    ? styles.cadenceDayDouble
                    : day.count > 0
                      ? styles.cadenceDayPosted
                      : ""
                }`}
                title={`${day.date}: ${day.count} post${day.count === 1 ? "" : "s"}`}
              />
            ))}
          </div>
          <div className={styles.cadenceLegend}>
            <div className={styles.cadenceLegendItem}><div className={`${styles.cadenceLegendBox} ${styles.cadenceLegendEmpty}`} />No post</div>
            <div className={styles.cadenceLegendItem}><div className={`${styles.cadenceLegendBox} ${styles.cadenceLegendPosted}`} />1 post</div>
            <div className={styles.cadenceLegendItem}><div className={`${styles.cadenceLegendBox} ${styles.cadenceLegendDouble}`} />2+ posts</div>
          </div>
          <div className={styles.cadenceFlag}>{buildCadenceFlag(cadence)}</div>
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionHdr}>
          <div className={styles.sectionTitle}>Content performance summary</div>
          <div className={styles.sectionSub}>{fmt.int(perf?.summary?.post_count)} {isInstagram ? "posts" : "videos"} in {windowLabel}</div>
        </div>
        <div className={styles.summaryStrip}>
          {summaryStrip.map((item) => (
            <div key={item.label} className={styles.summaryItem}>
              <div className={styles.summaryLabel}>{item.label}</div>
              <div className={styles.summaryValue}>{renderMetricValue(item)}</div>
            </div>
          ))}
        </div>
        <div className={styles.bwGrid}>
          <BestWorstCard title="Best performing post" post={perf.best_post} isInstagram={isInstagram} />
          <BestWorstCard title="Worst performing post" post={perf.worst_post} isInstagram={isInstagram} worst />
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionHdr}>
          <div className={styles.sectionTitle}>All posts performance</div>
          <div className={styles.sectionSub}>Sorted by most recent</div>
        </div>
        <div className={styles.tableWrap}>
          <table className={styles.postTable}>
            <thead>
              <tr>
                <th>{isInstagram ? "Post" : "Video"}</th>
                {isInstagram ? <th>Type</th> : null}
                {!isInstagram ? <th className={styles.num}>Views</th> : null}
                <th className={styles.num}>Likes</th>
                <th className={styles.num}>Comments</th>
                {!isInstagram ? <th className={styles.num}>Shares</th> : null}
                <th className={styles.num}>{isInstagram ? "Engagement rate" : "Eng/View"}</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {visiblePosts.length ? visiblePosts.map((post) => (
                <tr
                  key={post.id || `${post.date}-${post.title}`}
                  className={post.permalink ? styles.postRowLink : ""}
                  onClick={post.permalink ? () => window.open(post.permalink, "_blank", "noopener,noreferrer") : undefined}
                >
                  <td>
                    <div className={styles.postCell}>
                      <PostThumb post={post} square={isInstagram} />
                      <div>
                        <div className={styles.postCaption}>{post.caption || post.title || "Untitled post"}</div>
                        <div className={styles.postDate}>{post.date_label || fmt.date(post.date)}</div>
                      </div>
                    </div>
                  </td>
                  {isInstagram ? <td><span className={styles.postTypeTag}>{post.type || EMPTY}</span></td> : null}
                  {!isInstagram ? <td className={styles.num}>{fmt.int(post.views)}</td> : null}
                  <td className={styles.num}>{fmt.int(post.likes)}</td>
                  <td className={styles.num}>{fmt.int(post.comments)}</td>
                  {!isInstagram ? <td className={styles.num}>{fmt.int(post.shares)}</td> : null}
                  <td className={styles.num}>{fmt.pct(post.engagement_rate, 2)}</td>
                  <td>{post.date_label || fmt.date(post.date)}</td>
                </tr>
              )) : (
                <tr><td colSpan={isInstagram ? 6 : 7} className={styles.emptyState}>No posts detected in this window.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {hasMore && (
          <button
            type="button"
            className={styles.showMoreBtn}
            onClick={() => setVisibleCount((c) => c + POSTS_PAGE_SIZE)}
          >
            Show 10 more · {posts.length - visibleCount} remaining
          </button>
        )}
      </div>
    </div>
  );
}

export default function SocialMediaReportViewer({ data = {}, report = {}, onWindowChange }) {
  const defaultWindowKey = data?.default_window_key || null;
  const initialWindowList = useMemo(() => getSocialWindowList(data), [data]);
  const [activeWindowKey, setActiveWindowKey] = useState(
    defaultWindowKey || initialWindowList[0]?.key || null
  );
  useEffect(() => {
    if (!initialWindowList.length) {
      setActiveWindowKey(null);
      return;
    }
    setActiveWindowKey((currentKey) => {
      const stillExists = initialWindowList.some((entry) => entry.key === currentKey);
      if (stillExists) return currentKey;
      return defaultWindowKey || initialWindowList[0]?.key || null;
    });
  }, [defaultWindowKey, initialWindowList]);

  useEffect(() => {
    if (!activeWindowKey || !onWindowChange) return;
    const entry = initialWindowList.find((w) => w.key === activeWindowKey);
    onWindowChange({
      key: activeWindowKey,
      label: entry?.label || activeWindowKey,
      days: entry?.days || null,
      startDate: entry?.startDate || null,
      endDate: entry?.endDate || null,
      reportId: report?._id || report?.id || null,
      generationKey: data?.generation_key || null,
      reportKey: "socialMedia",
      collection: "social_media_report",
      section: "report",
      windowKey: activeWindowKey,
      assessmentKey: "social",
      assessmentLabel: "Social Media Report",
    });
  }, [activeWindowKey, data?.generation_key, initialWindowList, onWindowChange, report?._id, report?.id]);

  const activeWindowData = useMemo(
    () => getActiveSocialWindow(data, activeWindowKey),
    [data, activeWindowKey]
  );

  const viewerContent =
    activeWindowData?.social_media_report_viewer_content ||
    data?.social_media_report_viewer_content ||
    null;
  const legacyTabs = useMemo(
    () =>
      [
        buildLegacyReportTab(activeWindowData?.platforms?.tiktok || data?.platforms?.tiktok),
        buildLegacyReportTab(activeWindowData?.platforms?.instagram || data?.platforms?.instagram),
      ].filter(Boolean),
    [
      activeWindowData?.platforms?.instagram,
      activeWindowData?.platforms?.tiktok,
      data?.platforms?.instagram,
      data?.platforms?.tiktok,
    ]
  );

  const tabs = Array.isArray(viewerContent?.tabs) && viewerContent.tabs.length
    ? viewerContent.tabs
    : legacyTabs;
  const [activeKey, setActiveKey] = useState(viewerContent?.default_tab || tabs[0]?.key || "tiktok");
  useEffect(() => {
    if (!tabs.length) {
      setActiveKey(null);
      return;
    }
    setActiveKey((currentKey) => {
      const stillExists = tabs.some((tab) => tab?.key === currentKey);
      if (stillExists) return currentKey;
      return viewerContent?.default_tab || tabs[0]?.key || "tiktok";
    });
  }, [viewerContent?.default_tab, tabs]);
  const activeTab = tabs.find((tab) => tab.key === activeKey) || tabs[0] || null;
  const timeframe =
    viewerContent?.timeframe ||
    activeWindowData?.timeframe ||
    data?.timeframe ||
    {};
  const windowLabel =
    initialWindowList.find((windowEntry) => windowEntry.key === activeWindowKey)?.label ||
    activeWindowData?.label ||
    activeWindowData?.timeframe?.window?.label ||
    timeframe?.window?.label ||
    timeframe?.label ||
    "Selected window";

  console.log("[SocialMediaReportViewer] render", {
    reportId: report?._id || report?.id || null,
    collectionName: data?.collection_name || null,
    hasViewerContent: Boolean(viewerContent),
    activeWindowKey,
    availableWindowKeys: initialWindowList.map((windowEntry) => windowEntry?.key || null),
    tabs: tabs.map((tab) => tab.key),
    activeKey,
  });

  if (!activeTab) {
    return <div className={styles.viewer}><div className={styles.emptyState}>No social media account data available yet.</div></div>;
  }

  return (
    <div className={styles.viewer}>
      <div className={styles.tfBar}>
        {initialWindowList.length ? (
          <select
            className={styles.windowPickerSelect}
            value={activeWindowKey || ""}
            onChange={(e) => setActiveWindowKey(e.target.value)}
            aria-label="Social media report time window"
          >
            {initialWindowList.map((windowEntry) => (
              <option key={windowEntry.key} value={windowEntry.key}>
                {windowEntry.label}
              </option>
            ))}
          </select>
        ) : null}
        {timeframe?.why ? <div className={styles.tfWhy}>{timeframe.why}</div> : null}
      </div>

      <div className={styles.tabBar}>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`${styles.tabBtn} ${activeKey === tab.key ? styles.tabBtnActive : ""}`}
            onClick={() => setActiveKey(tab.key)}
          >
            <TabIcon type={tab.key} />
            {tab.label}
          </button>
        ))}
      </div>

      <div className={styles.body}>
        <PlatformView tab={activeTab} windowLabel={windowLabel} />
      </div>
    </div>
  );
}
